import React, { Fragment, useState } from 'react';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import DropdownMenu, {
	DropdownItem,
	DropdownItemCheckbox,
	DropdownItemCheckboxGroup,
	DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import BugIcon from '@atlaskit/icon/core/bug';
import EditIcon from '@atlaskit/icon/core/edit';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import Tag from '@atlaskit/tag';

import { OnlineDebuggerModal } from '../../online-debugger/online-debugger-modal';
import { PromptOverrideModal } from '../../online-debugger/prompt-override-modal';

const messages = defineMessages({
	debugger_menu_item: {
		id: 'search_ai.debugger_menu_item',
		defaultMessage: 'Debugger',
		description: 'Menu item label for the debugger button in the overflow menu',
	},
	prompt_override_menu_item: {
		id: 'search_ai.prompt_override_menu_item',
		defaultMessage: 'Prompt override',
		description: 'Menu item label for the prompt override button in the overflow menu',
	},
	store_message_label: {
		id: 'search_ai.store_message_label',
		defaultMessage: 'Record this message to help us improve answer quality',
		description: 'Label for the store message checkbox',
	},
	internal_lozenge: {
		id: 'search_ai.internal_lozenge',
		defaultMessage: 'INTERNAL',
		description: 'Label for the internal lozenge indicating this feature is for internal use only',
	},
});

const DROPDOWN_Z_INDEX = 900;

const dropdownPanelStyles = cssMap({
	panel: {
		width: '350px',
	},
	lozengeWrapper: {
		width: 'fit-content',
	},
});

export function SAINMoreOptionsDropdown(): React.JSX.Element {
	const [isDebuggerModalOpen, setIsDebuggerModalOpen] = useState<boolean>(false);
	const [isPromptOverrideModalOpen, setIsPromptOverrideModalOpen] = useState<boolean>(false);
	const isArizeGateOn = fg('kd_sain_enable_arize_hello_only');
	const showOnlineDebugger = fg('kd_sain_online_debugger');
	const showPromptOverride = fg('convo_ai_sain_enable_prompt_override_hello_only');
	const { formatMessage } = useIntl();
	const [storeMessage, setStoreMessage] = React.useState<boolean>(() => {
		try {
			// TODO(MIGRATE-STORAGE-BASELINE): migrate to @atlassian/search-common/local-storage wrapper
			// eslint-disable-next-line @atlassian/search-common/no-direct-local-storage
			const stored = localStorage.getItem('search-ai.sain.store_message');
			return stored === null || stored === 'true';
		} catch {
			return true;
		}
	});

	const onStoreMessageToggle = () => {
		const next = !storeMessage;
		setStoreMessage(next);
		try {
			// TODO(MIGRATE-STORAGE-BASELINE): migrate to @atlassian/search-common/local-storage wrapper
			// eslint-disable-next-line @atlassian/search-common/no-direct-local-storage
			localStorage.setItem('search-ai.sain.store_message', String(next));
		} catch {
			// ignore
		}
	};

	const onMoreOptionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
	};

	const onDebuggerClick = () => {
		setIsDebuggerModalOpen(true);
	};

	const onPromptOverrideClick = () => {
		setIsPromptOverrideModalOpen(true);
	};

	return (
		<Fragment>
			<DropdownMenu
				trigger={({ triggerRef, ...props }) => (
					<IconButton
						{...props}
						ref={triggerRef}
						icon={ShowMoreHorizontalIcon}
						label="More options"
						appearance="subtle"
						spacing="compact"
						testId="sain-more-options-button"
						onClick={(e) => {
							onMoreOptionsClick(e);
							props.onClick?.(e);
						}}
					/>
				)}
				placement="bottom-end"
				zIndex={DROPDOWN_Z_INDEX}
			>
				<Box xcss={dropdownPanelStyles.panel}>
					<DropdownItemGroup>
						{showOnlineDebugger && (
							<DropdownItem
								elemBefore={<BugIcon label="" />}
								onClick={onDebuggerClick}
								testId="online-debugger-button"
							>
								{formatMessage(messages.debugger_menu_item)}
							</DropdownItem>
						)}
						{showPromptOverride && (
							<DropdownItem
								elemBefore={<EditIcon label="" />}
								onClick={onPromptOverrideClick}
								testId="prompt-override-button"
							>
								<FormattedMessage {...messages.prompt_override_menu_item} />
							</DropdownItem>
						)}
					</DropdownItemGroup>
					{isArizeGateOn && (
						<>
							<DropdownItemCheckboxGroup id="sain-store-message-group">
								<DropdownItemCheckbox
									id="sain-store-message"
									isSelected={storeMessage}
									onClick={onStoreMessageToggle}
									testId="sain-store-message-checkbox"
								>
									<Stack space="space.075">
										<FormattedMessage {...messages.store_message_label} />
										<Box xcss={dropdownPanelStyles.lozengeWrapper}>
											<Tag
												text={formatMessage(messages.internal_lozenge)}
												isRemovable={false}
												migration_fallback="lozenge"
											/>
										</Box>
									</Stack>
								</DropdownItemCheckbox>
							</DropdownItemCheckboxGroup>
						</>
					)}
				</Box>
			</DropdownMenu>
			{showOnlineDebugger && (
				<OnlineDebuggerModal
					isOpen={isDebuggerModalOpen}
					onClose={() => setIsDebuggerModalOpen(false)}
				/>
			)}
			{showPromptOverride && (
				<PromptOverrideModal
					isOpen={isPromptOverrideModalOpen}
					onClose={() => setIsPromptOverrideModalOpen(false)}
				/>
			)}
		</Fragment>
	);
}
